// table — default skin module (RUN-407: native table redesign)
// Renders native <table>/<thead>/<tbody>/<tr>/<th>/<td> structure.
// Column header content is projected via manual slot assignment (browser-only).
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

// Guard: manual slot assignment API is not available in jsdom.
// Structural output (native table elements) is correct in all environments.
// Slot content projection is browser-only.
const _MANUAL_SLOT_SUPPORTED =
  typeof HTMLSlotElement !== 'undefined' &&
  typeof HTMLSlotElement.prototype.assign === 'function';

const SVG_UNSORTED = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2L9 5H3L6 2Z" fill="currentColor"/><path d="M6 10L3 7H9L6 10Z" fill="currentColor"/></svg>`;
const SVG_ASC     = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 2L10 7H2L6 2Z" fill="currentColor"/></svg>`;
const SVG_DESC    = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M6 10L2 5H10L6 10Z" fill="currentColor"/></svg>`;

export const styles = `
  :host {
    display: block;
    position: relative;
    background: var(--run-table-background, var(--run-color-surface-default));
    width: 100%;
    box-sizing: border-box;
    overflow-x: auto;
  }

  [part="base"] {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;
  }

  :host([bordered]) [part="base"] {
    border: 1px solid var(--run-table-border-color, var(--run-color-border-default));
  }

  [part="loading-overlay"] {
    display: none;
    position: absolute;
    inset: 0;
    background: var(--run-table-overlay-background, rgba(0,0,0,0.45));
    z-index: 1;
  }

  :host([loading]) [part="loading-overlay"] {
    display: block;
  }

  /* --- Header cells --- */
  [part="th"],
  [part="th-select"] {
    padding: var(--_run-table-cell-padding-v, 8px) var(--_run-table-cell-padding-h, 12px);
    font-size: var(--_run-table-font-size, var(--run-font-size-sm));
    font-weight: var(--run-font-semantic-weight-strong, 600);
    color: var(--run-table-header-color, var(--run-color-text-primary));
    background: var(--run-table-header-bg, var(--run-color-surface-subtle));
    border-bottom: 1px solid
      var(--run-table-header-border-color, var(--run-color-border-default));
    white-space: nowrap;
    user-select: none;
    text-align: start;
    vertical-align: middle;
  }

  [part="th-select"] {
    width: 40px;
  }

  [part="th"][data-sortable] {
    cursor: pointer;
  }

  [part="th"][data-sortable]:hover {
    background: var(--run-color-state-hover-overlay);
  }

  :host([bordered]) [part="th"] {
    border-right: 1px solid
      var(--run-table-column-border-color, var(--run-color-divider));
  }

  :host([bordered]) tr [part="th"]:last-child {
    border-right: none;
  }

  /* --- Header content layout --- */
  .th-content {
    display: flex;
    align-items: center;
    gap: var(--run-space-semantic-xs, 4px);
  }

  .th-label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .th-sort-indicator {
    flex-shrink: 0;
    color: var(--run-color-icon-secondary);
    line-height: 0;
  }

  [part="th"][data-sort-direction] .th-sort-indicator {
    color: var(--run-color-icon-primary);
  }

  /* --- Select-all cell --- */
  .select-all-inner {
    display: flex;
    align-items: center;
  }

  .select-all-inner run-checkbox,
  [part="td-select"] run-checkbox {
    --run-space-semantic-xs: 0px;
  }

  .select-all-inner run-checkbox::part(base),
  [part="td-select"] run-checkbox::part(base) {
    min-height: auto;
    min-width: auto;
    padding: 0;
    gap: 0;
  }

  /* --- Data cells --- */
  [part="td"],
  [part="td-select"] {
    padding: var(--_run-table-cell-padding-v, 8px) var(--_run-table-cell-padding-h, 12px);
    font-size: var(--_run-table-font-size, var(--run-font-size-sm));
    border-bottom: 1px solid
      var(--run-table-row-border-color, var(--run-color-divider));
    vertical-align: middle;
    background: var(--_run-table-row-bg, var(--run-table-row-bg, var(--run-color-surface-default)));
    transition: background var(--run-motion-semantic-micro-duration, 150ms)
      var(--run-motion-semantic-micro-easing, ease);
  }

  [part="td-select"] {
    width: 40px;
  }

  :host([bordered]) [part="td"] {
    border-right: 1px solid
      var(--run-table-column-border-color, var(--run-color-divider));
  }

  :host([bordered]) tr [part="td"]:last-child {
    border-right: none;
  }

  /* --- Row states --- */
  [part="tr"]:not([data-disabled]):hover [part="td"],
  [part="tr"]:not([data-disabled]):hover [part="td-select"] {
    background: var(--run-table-row-bg-hover, var(--run-color-state-hover-overlay));
  }

  [part="tr"][data-selected] [part="td"],
  [part="tr"][data-selected] [part="td-select"] {
    background: var(--run-table-row-bg-selected, var(--run-color-state-selected-overlay));
  }

  [part="tr"][data-disabled] [part="td"],
  [part="tr"][data-disabled] [part="td-select"] {
    opacity: var(--run-opacity-state-disabled, 0.5);
    cursor: not-allowed;
  }

  [part="tr"][data-clickable]:not([data-disabled]):hover {
    cursor: pointer;
  }

  /* --- Skeleton loading --- */
  [part="tr-skeleton"] td {
    padding: 0;
    border-bottom: none;
  }

  /* --- Cascade entrance for rows beyond the VT-named skeleton count --- */
  @keyframes run-table-row-cascade {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: none; }
  }

  [part="tr"][data-cascade-enter] {
    animation: run-table-row-cascade 250ms cubic-bezier(0, 0, 0.2, 1)
      calc(var(--_cascade-delay, 0ms)) both;
  }
`;

export function render(host) {
  const { selects, clickable, striped, loading } = host;
  const colEls = host._columns;
  const rowEls = host._rows;

  const hasMultiSelect = selects === 'multiple';
  const colCount = colEls.length;

  // ── <colgroup> ─────────────────────────────────────────────────────────────
  let colgroupHtml = '';
  if (colCount > 0) {
    const colItems = colEls.map((col, i) => {
      const w = (col.width != null ? String(col.width) : null) ?? col.getAttribute('width');
      // <col> only supports absolute widths — fr units are not valid; omit for auto
      if (w && /^\d+(\.\d+)?px$/.test(w.trim())) {
        return `<col data-col-idx="${i}" style="width:${w.trim()}">`;
      }
      return `<col data-col-idx="${i}">`;
    }).join('');
    colgroupHtml = `<colgroup>${colItems}</colgroup>`;
  }

  // ── <thead> ────────────────────────────────────────────────────────────────
  let theadHtml = '';
  if (colCount > 0) {
    const selectThHtml = hasMultiSelect ? `
      <th part="th-select" scope="col">
        <div class="select-all-inner">
          <run-checkbox
            part="select-all-checkbox"
            ${host._getSelectAllState() === 'checked' ? 'checked' : ''}
            ${host.selectAllIndeterminate ? 'indeterminate' : ''}
            aria-label="Select all rows"
          ></run-checkbox>
        </div>
      </th>` : '';

    const colHeaders = colEls.map((col, i) => {
      const sortable = col.sortable === true;
      const dir = col.sortDirection;
      const align = col.align || 'start';
      const justifyMap = { start: 'flex-start', center: 'center', end: 'flex-end' };
      const justify = justifyMap[align] || 'flex-start';

      let ariaSort = '';
      let sortSvg = SVG_UNSORTED;
      if (sortable) {
        if (dir === 'asc')       { ariaSort = 'ascending';  sortSvg = SVG_ASC;  }
        else if (dir === 'desc') { ariaSort = 'descending'; sortSvg = SVG_DESC; }
        else                     { ariaSort = 'none'; }
      }

      const attrs = [
        'part="th"',
        'scope="col"',
        `data-col-idx="${i}"`,
        `data-align="${align}"`,
        sortable ? 'data-sortable'               : '',
        dir      ? `data-sort-direction="${dir}"` : '',
        ariaSort ? `aria-sort="${ariaSort}"`      : '',
        sortable ? 'tabindex="0"'                 : '',
      ].filter(Boolean).join(' ');

      const sortIndicator = sortable
        ? `<span class="th-sort-indicator">${sortSvg}</span>` : '';

      return `
        <th ${attrs}>
          <div class="th-content" style="justify-content:${justify}">
            <span class="th-label"><slot name="col-${i}"></slot></span>
            ${sortIndicator}
          </div>
        </th>`;
    }).join('');

    theadHtml = `<thead part="thead"><tr part="tr-head">${selectThHtml}${colHeaders}</tr></thead>`;
  }

  // ── <tbody> ────────────────────────────────────────────────────────────────
  let tbodyHtml = '';
  const totalCols = (hasMultiSelect ? 1 : 0) + Math.max(colCount, 1);

  if (loading && rowEls.length === 0) {
    const skeletonRow = (i) => `
      <tr part="tr-skeleton" style="view-transition-name: table-row-${i}">
        <td colspan="${totalCols}"><run-skeleton></run-skeleton></td>
      </tr>`;
    tbodyHtml = `<tbody part="tbody">${skeletonRow(0)}${skeletonRow(1)}${skeletonRow(2)}</tbody>`;
  } else if (rowEls.length > 0) {
    // Stagger interval per §5 of yensid-motion-contract-v1.md
    const VT_ROW_COUNT = 3;
    const rowCount = rowEls.length;
    const cascadeInterval = rowCount <= 5  ? 60 :
                            rowCount <= 10 ? 40 :
                            rowCount <= 20 ? 25 :
                            Math.floor(500 / rowCount);
    // Viewport gate: skip cascade animation for rows that can't be visible
    const viewportRowLimit = typeof window !== 'undefined' && window.innerHeight > 0
      ? Math.ceil(window.innerHeight / 40) + 4
      : 999;

    const rowParts = rowEls.map((row, ri) => {
      const isSelected = row.selected === true;
      const isDisabled = row.disabled === true;

      // Merge stripe, VT name, and cascade stagger into one style attribute
      const styleProps = [];
      if (striped && !isSelected && ri % 2 === 1) {
        styleProps.push('--_run-table-row-bg: var(--run-table-row-bg-stripe, var(--run-color-surface-subtle))');
      }
      if (ri < VT_ROW_COUNT) {
        styleProps.push(`view-transition-name: table-row-${ri}`);
      } else if (ri < viewportRowLimit) {
        styleProps.push(`--_cascade-delay: ${ri * cascadeInterval}ms`);
      }
      const styleAttr = styleProps.length ? ` style="${styleProps.join(';')}"` : '';
      const cascadeAttr = (ri >= VT_ROW_COUNT && ri < viewportRowLimit) ? ' data-cascade-enter' : '';

      const rowAttrs = [
        'part="tr"',
        `data-row-idx="${ri}"`,
        isSelected ? 'data-selected'                         : '',
        isDisabled ? 'data-disabled'                         : '',
        clickable  ? 'data-clickable'                        : '',
        isSelected ? 'aria-selected="true"'                  : (selects ? 'aria-selected="false"' : ''),
        isDisabled ? 'aria-disabled="true"'                  : '',
      ].filter(Boolean).join(' ');

      const selectTdHtml = hasMultiSelect ? `
        <td part="td-select">
          <run-checkbox
            ${isSelected ? 'checked' : ''}
            ${isDisabled ? 'disabled' : ''}
            aria-label="Select row"
          ></run-checkbox>
        </td>` : '';

      const dataCells = colEls.map((col, ci) => {
        const align = col.align || 'start';
        return `<td part="td" data-row-idx="${ri}" data-col-idx="${ci}" data-align="${align}"><slot name="row-${ri}-col-${ci}"></slot></td>`;
      }).join('');

      // Fallback when no columns defined: render cells from run-table-cell children
      const cellEls = colCount === 0
        ? Array.from(row.querySelectorAll('run-table-cell'))
        : [];
      const fallbackCells = cellEls.map((_, ci) =>
        `<td part="td" data-row-idx="${ri}" data-col-idx="${ci}"><slot name="row-${ri}-col-${ci}"></slot></td>`
      ).join('');

      return `<tr ${rowAttrs}${styleAttr}${cascadeAttr}>${selectTdHtml}${dataCells || fallbackCells}</tr>`;
    }).join('');

    tbodyHtml = `<tbody part="tbody">${rowParts}</tbody>`;
  }

  // ── Write shadow DOM ───────────────────────────────────────────────────────
  host.shadowRoot.innerHTML = `
    <table part="base">
      ${colgroupHtml}
      ${theadHtml}
      ${tbodyHtml}
    </table>
    <div part="loading-overlay" aria-hidden="true"></div>
  `;

  // ── Manual slot assignments (browser-only) ─────────────────────────────────
  if (_MANUAL_SLOT_SUPPORTED) {
    colEls.forEach((col, i) => {
      const slot = host.shadowRoot.querySelector(`slot[name="col-${i}"]`);
      if (slot) slot.assign(col);
    });
    rowEls.forEach((row, ri) => {
      const cellEls = Array.from(row.querySelectorAll('run-table-cell'));
      colEls.forEach((_, ci) => {
        const slot = host.shadowRoot.querySelector(`slot[name="row-${ri}-col-${ci}"]`);
        if (slot && cellEls[ci]) slot.assign(cellEls[ci]);
      });
      // Fallback: no columns defined
      if (colCount === 0) {
        const fallbackCells = Array.from(row.querySelectorAll('run-table-cell'));
        fallbackCells.forEach((cell, ci) => {
          const slot = host.shadowRoot.querySelector(`slot[name="row-${ri}-col-${ci}"]`);
          if (slot) slot.assign(cell);
        });
      }
    });
  }

  // ── Wire select-all checkbox ───────────────────────────────────────────────
  if (hasMultiSelect) {
    const selectAllCb = host.shadowRoot.querySelector('[part="select-all-checkbox"]');
    if (selectAllCb) {
      selectAllCb.addEventListener('change', (e) => {
        host._handleSelectAllChange(e.target.checked ?? selectAllCb.checked);
      });
    }

    // Wire per-row checkboxes
    const rowCbs = host.shadowRoot.querySelectorAll('[part="td-select"] run-checkbox');
    rowEls.forEach((row, ri) => {
      const cb = rowCbs[ri];
      if (cb) {
        cb.addEventListener('change', (e) => {
          host._handleRowCheckboxChange(row, e.target.checked ?? cb.checked);
        });
      }
    });
  }

  // ── Wire sort interactions on <th> elements ────────────────────────────────
  colEls.forEach((col, ci) => {
    if (!col.sortable) return;
    const th = host.shadowRoot.querySelector(`[part="th"][data-col-idx="${ci}"]`);
    if (!th) return;
    // Click: handle at <th> level for full-area coverage.
    // Guard against double-fire when click originated from within the slotted column element.
    th.addEventListener('click', (e) => {
      if (_MANUAL_SLOT_SUPPORTED && e.composedPath().includes(col)) return;
      col._handleClick();
    });
    // Keydown: <th> has tabindex=0; keyboard focus lands on <th>, not on slotted column.
    th.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        col._handleClick();
      }
    });
  });
}
